import { Box, Button, SxProps, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CustomTypographyVariant } from 'theme/theme';

interface AutoTruncatingContainerWithShowMoreProps {
  text: string;
  variant: CustomTypographyVariant;
  sx?: SxProps;
  clampValue?: number;
}

const AutoTruncatingContainerWithShowMore = ({
  text,
  variant,
  sx,
  clampValue = 2,
}: AutoTruncatingContainerWithShowMoreProps) => {
  const textRef = useRef<HTMLDivElement>(null);

  const [currentClamp, setCurrentClamp] = useState<number | undefined>(clampValue);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current && !isExpanded) {
        const isTruncated = textRef.current.scrollHeight > textRef.current.clientHeight;
        setIsTextTruncated(isTruncated);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [textRef, isExpanded]);

  const onToggleClamp = useCallback(() => {
    if (currentClamp) {
      setCurrentClamp(undefined);
      setIsExpanded(true);
      return;
    }

    setCurrentClamp(clampValue);
    setIsExpanded(false);
  }, [currentClamp, clampValue]);

  return (
    <Box>
      <Typography
        variant={variant}
        ref={textRef}
        sx={{
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: currentClamp,
          WebkitBoxOrient: 'vertical',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
          wordWrap: 'break-word',
          hyphens: 'auto',
          whiteSpace: 'pre-wrap',
          marginBottom: 2,
          ...sx,
        }}
      >
        {text}
      </Typography>
      {isTextTruncated && (
        <Button variant="outlined" onClick={onToggleClamp} sx={{ marginBottom: 1 }}>
          {currentClamp ? 'More' : 'Less'}
        </Button>
      )}
    </Box>
  );
};

export default AutoTruncatingContainerWithShowMore;
